import { useState, useEffect } from 'react'
import { Container, Flex, Heading, Callout, Select, Text } from '@radix-ui/themes'
import { CircleBackslashIcon } from '@radix-ui/react-icons'

import { TransferCard } from '../components/TransferCard'

import { Transfer, TRANSFER_STATUS_LABELS } from '@/interfaces/Transfer'
import { useAuth } from '@/hooks/useAuth'
import useTransfersFetcher from '@/hooks/useTransfersFetcher'
import useTransferUpdates from '@/hooks/useTransferUpdates'
import { UserProfileRole } from '@/modules/users/types/UserProfile'

export const TransfersScreen = () => {
  const { hasAnyRole, userData } = useAuth()
  const { fetchTransfers, loading, errorTransfers } = useTransfersFetcher()
  const organizationId = userData?.organization?._id

  const [transfers, setTransfers] = useState<Transfer[]>([])
  const [total, setTotal] = useState(0)
  const [statusFilter, setStatusFilter] = useState<string>('all')

  // SSE real-time updates
  const { latestUpdates } = useTransferUpdates(organizationId)

  const canView = hasAnyRole(UserProfileRole.EditTransfers, UserProfileRole.ViewTransfers)

  const loadTransfers = async () => {
    if (!organizationId) return
    const filter = statusFilter === 'all' ? undefined : statusFilter
    const result = await fetchTransfers(organizationId, filter)

    if (result) {
      setTransfers(result.transfers)
      setTotal(result.total)
    }
  }

  useEffect(() => {
    loadTransfers()
  }, [organizationId, statusFilter])

  // Refresh when SSE updates arrive
  useEffect(() => {
    if (latestUpdates.length > 0) {
      loadTransfers()
    }
  }, [latestUpdates])

  if (!canView) {
    return (
      <Container p="4">
        <Callout.Root color="red">
          <Callout.Icon>
            <CircleBackslashIcon />
          </Callout.Icon>
          <Callout.Text>No tienes permisos para ver transferencias.</Callout.Text>
        </Callout.Root>
      </Container>
    )
  }

  return (
    <Flex direction="column" gap="3" width="100%" overflow="auto" p="4">
      <Flex justify="between" align="center">
        <Heading size="5">Transferencias</Heading>
        <Flex gap="2" align="center">
          <Text size="2" color="gray">
            Filtrar:
          </Text>
          <Select.Root value={statusFilter} onValueChange={(value) => setStatusFilter(value)}>
            <Select.Trigger />
            <Select.Content>
              <Select.Item value="all">Todos</Select.Item>
              {Object.entries(TRANSFER_STATUS_LABELS).map(([key, label]) => (
                <Select.Item key={key} value={key}>
                  {label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Flex>
      </Flex>

      {errorTransfers && (
        <Callout.Root color="red">
          <Callout.Text>{errorTransfers.message}</Callout.Text>
        </Callout.Root>
      )}

      {loading && transfers.length === 0 && (
        <Text size="2" color="gray">
          Cargando transferencias...
        </Text>
      )}

      {!loading && transfers.length === 0 && (
        <Flex direction="column" align="center" justify="center" gap="2" className="py-16">
          <Text size="3" color="gray">
            No hay transferencias{statusFilter !== 'all' ? ' con este estado' : ''}
          </Text>
          <Text size="2" color="gray">
            Las transferencias se inician desde notas de venta aprobadas.
          </Text>
        </Flex>
      )}

      <Flex direction="column" gap="2">
        {transfers.map((transfer) => (
          <TransferCard key={transfer._id} transfer={transfer} />
        ))}
      </Flex>

      {total > 0 && (
        <Text size="1" color="gray" align="center">
          Mostrando {transfers.length} de {total} transferencias
        </Text>
      )}
    </Flex>
  )
}
